// src/components/SeatDensityBar.jsx
//
// OFFICES-DENSITY-AND-COLUMNS (mock Option A): reusable SF / seat range bar.
// Presentation only. Density = rentable sf ÷ installed seats, unrounded.
// Track 0..400, typical band 150..250, amber tick at the effective policy floor.
import React from "react";
import { T } from "../app/tokens.js";
import { getDisplayedSqft } from "../utils/locationSqftDerived.js";

export const DENSITY_TRACK_MAX = 400;
export const DENSITY_BAND_MIN = 150;
export const DENSITY_BAND_MAX = 250;

/** Pure: unrounded sf per installed seat, or null when inputs are missing/nonpositive. */
export function sfPerInstalledSeat(sf, seats) {
  const s = sf == null || sf === "" ? NaN : Number(sf);
  const n = seats == null || seats === "" ? NaN : Number(seats);
  if (!Number.isFinite(s) || s < 0 || !Number.isFinite(n) || n <= 0) return null;
  return s / n;
}

/** Ratio helper for callers with the existing rentable-area projection. */
export const seatDensityValue = sfPerInstalledSeat;

/** Same existing displayed rentable-area resolver as getSpaceFitReport. */
export function officeSeatDensity(location) {
  if (!location) return null;
  return sfPerInstalledSeat(getDisplayedSqft(location, null), location.installedCapacity);
}

/** Strict unrounded comparison; null floor never flags. */
export function isBelowDensityFloor(value, floor) {
  return value != null && Number.isFinite(floor) && value < floor;
}

export function seatDensityTooltip(value, floor) {
  if (value == null) return "No rentable sf or installed seats on file";
  const f = Number.isFinite(floor) ? ` · floor ${floor}` : "";
  return `${Math.round(value).toLocaleString()} sf per installed seat${f}`;
}

const pct = v => `${(Math.max(0, Math.min(v, DENSITY_TRACK_MAX)) / DENSITY_TRACK_MAX) * 100}%`;

export function SeatDensityBar({ value, floor, width = 112, "data-testid": testId = "seat-density-bar" }) {
  if (value == null || !Number.isFinite(value)) {
    return <span data-testid={testId} data-state="missing" title={seatDensityTooltip(null)} style={{ color: T.light }}>–</span>;
  }
  const below = isBelowDensityFloor(value, floor);
  const over = value > DENSITY_TRACK_MAX;
  const tip = seatDensityTooltip(value, floor);
  return (
    <span data-testid={testId} data-state={below ? "below-floor" : over ? "off-scale" : "ok"}
      title={tip} aria-label={tip} role="img"
      style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
      <span aria-hidden="true" style={{ position: "relative", width, height: 6, borderRadius: 3, background: T.lightGray, flexShrink: 0 }}>
        <span style={{ position: "absolute", top: 0, height: 6, borderRadius: 2, background: T.histfill,
          left: pct(DENSITY_BAND_MIN), width: `${((DENSITY_BAND_MAX - DENSITY_BAND_MIN) / DENSITY_TRACK_MAX) * 100}%` }} />
        {Number.isFinite(floor) && (
          <span data-testid={`${testId}-floor`} style={{ position: "absolute", top: -3, left: pct(floor), width: 2, height: 12,
            marginLeft: -1, borderRadius: 1, background: T.pendingAmber }} />
        )}
        <span data-testid={`${testId}-dot`} style={{ position: "absolute", top: -3, left: pct(value), width: 12, height: 12,
          marginLeft: -6, borderRadius: "50%", background: below ? T.pendingAmber : T.planCompFill,
          border: `2px solid ${T.card}`, boxShadow: `0 0 0 1px ${T.borderDark}` }} />
      </span>
      <span style={{ minWidth: 30, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: T.fsSm,
        color: below ? T.ragAmberText : T.dark, fontWeight: below ? T.fwSemibold : T.fwRegular }}>
        {Math.round(value).toLocaleString()}
      </span>
      {over && <span data-testid={`${testId}-over`} style={{ fontSize: T.fsXxs, color: T.light, marginLeft: -4 }}>›</span>}
    </span>
  );
}

export default SeatDensityBar;
